import {useCallback, useEffect, useReducer, useRef, type ChangeEvent, type FocusEvent, type FormEvent} from 'react';
import {validateForm, type FormErrors, type FormSchema, type FormValues} from '@/util/validation';

export type FormTouched<V extends FormValues> = {[K in keyof V]?: boolean};

export interface FormHelpers {
    resetForm: () => void;
}

export interface UseFormOptions<V extends FormValues> {
    initialValues: V;
    schema: FormSchema<V>;
    onSubmit: (values: V, helpers: FormHelpers) => void | Promise<void>;
    /** Reset the form whenever `initialValues` changes value (Formik's `enableReinitialize`). */
    enableReinitialize?: boolean;
}

export interface FormApi<V extends FormValues> {
    values: V;
    errors: FormErrors<V>;
    touched: FormTouched<V>;
    isSubmitting: boolean;
    handleChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleBlur: (event: FocusEvent<HTMLElement>) => void;
    setFieldValue: (field: keyof V & string, value: unknown) => void;
    setFieldTouched: (field: keyof V & string, isTouched?: boolean) => void;
    handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
    resetForm: () => void;
}

interface FormState<V extends FormValues> {
    values: V;
    errors: FormErrors<V>;
    touched: FormTouched<V>;
    isSubmitting: boolean;
}

const sameValues = (a: object, b: object) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Minimal replacement for Formik, keeping the behaviour the forms relied on:
 * - the whole form is re-validated after every change, blur and setFieldValue/setFieldTouched,
 *   always against the latest values (several setFieldValue calls in one handler see each other);
 * - a field's error is only shown once it is touched (see FieldError);
 * - submitting marks every field touched, validates, and calls onSubmit only when valid.
 */
export function useForm<V extends FormValues>({
    initialValues,
    schema,
    onSubmit,
    enableReinitialize = false
}: UseFormOptions<V>): FormApi<V> {
    const initialRef = useRef(initialValues);
    const stateRef = useRef<FormState<V>>({values: initialValues, errors: {}, touched: {}, isSubmitting: false});
    const [, rerender] = useReducer((count: number) => count + 1, 0);
    const mountedRef = useRef(true);

    // Kept in refs so the handlers below stay stable while always reading the latest props.
    const schemaRef = useRef(schema);
    schemaRef.current = schema;
    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const update = useCallback((patch: Partial<FormState<V>>) => {
        stateRef.current = {...stateRef.current, ...patch};
        if (mountedRef.current) rerender();
    }, []);

    const runValidation = useCallback(
        (values: V) => {
            const errors = validateForm(schemaRef.current, values);
            update({errors});
            return errors;
        },
        [update]
    );

    const setFieldValue = useCallback(
        (field: keyof V & string, value: unknown) => {
            const values = {...stateRef.current.values, [field]: value};
            update({values});
            runValidation(values);
        },
        [update, runValidation]
    );

    const setFieldTouched = useCallback(
        (field: keyof V & string, isTouched = true) => {
            update({touched: {...stateRef.current.touched, [field]: isTouched}});
            runValidation(stateRef.current.values);
        },
        [update, runValidation]
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const {name, id, value} = event.target;
            const field = (name || id) as keyof V & string;
            if (field) setFieldValue(field, value);
        },
        [setFieldValue]
    );

    // An input with neither name nor id (the phone number field) only re-validates, as Formik did.
    const handleBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            const target = event.target as HTMLInputElement;
            const field = (target.name || target.id) as keyof V & string;
            if (field) setFieldTouched(field, true);
            else runValidation(stateRef.current.values);
        },
        [setFieldTouched, runValidation]
    );

    const resetForm = useCallback(() => {
        update({values: initialRef.current, errors: {}, touched: {}, isSubmitting: false});
    }, [update]);

    const handleSubmit = useCallback(
        (event?: FormEvent<HTMLFormElement>) => {
            event?.preventDefault();
            const {values} = stateRef.current;
            const touched = Object.fromEntries(Object.keys(values).map((key) => [key, true])) as FormTouched<V>;
            update({touched, isSubmitting: true});

            const errors = runValidation(values);
            if (Object.keys(errors).length > 0) {
                update({isSubmitting: false});
                return;
            }

            // Called inside .then so a synchronous throw is caught too, as Formik did.
            Promise.resolve()
                .then(() => onSubmitRef.current(values, {resetForm}))
                .catch((reason) => {
                    // Same warning Formik printed for an onSubmit that throws.
                    console.warn('Warning: An unhandled error was caught from submitForm()', reason);
                })
                .finally(() => {
                    if (mountedRef.current) update({isSubmitting: false});
                });
        },
        [update, runValidation, resetForm]
    );

    // enableReinitialize: a new initial value (e.g. the visitor's country arriving) resets the form.
    useEffect(() => {
        if (!enableReinitialize || sameValues(initialRef.current, initialValues)) return;
        initialRef.current = initialValues;
        resetForm();
    }, [enableReinitialize, initialValues, resetForm]);

    const {values, errors, touched, isSubmitting} = stateRef.current;
    return {values, errors, touched, isSubmitting, handleChange, handleBlur, setFieldValue, setFieldTouched, handleSubmit, resetForm};
}
